/**
 * Central registry of every unique YouTube video embedded on the site.
 *
 * Each entry powers a dedicated watch page at /videos/<slug> so the video
 * can appear in Google's video tab from our own domain. The slug is
 * URL-safe and stable; the id is the 11-char YouTube video ID; uploadDate
 * is the video's real YouTube upload date (ISO 8601) where known, else
 * omitted (never fabricated).
 *
 * VideoObject schema is built from this record in buildVideoObject().
 */
export interface VideoChapter {
  /** Chapter title (shown as a jump link + in VideoObject "hasPart"). */
  name: string;
  /** Start time in seconds. */
  startSeconds: number;
}

export interface VideoRecord {
  /** URL-safe, stable slug used in /videos/<slug>. */
  slug: string;
  /** 11-character YouTube video ID (the part after /embed/). */
  id: string;
  /** Human title (used as H1 + VideoObject.name + og:title). */
  title: string;
  /** Short description for the watch page + VideoObject.description. */
  description: string;
  /** Category label for grouping on the /videos index. */
  category: "SAINT TMS" | "Prism" | "Patient Stories" | "TMS Education";
  /** ISO 8601 upload date if known; omitted otherwise (never fabricated). */
  uploadDate?: string;
  /** ISO 8601 duration if known (e.g. "PT5M30S"); omitted otherwise. */
  duration?: string;
  /** YouTube chapters / key moments for deep-linking + VideoObject "hasPart". */
  chapters?: VideoChapter[];
  /** Optional journey-section content for the dedicated watch page. */
  journeySections?: JourneySection[];
}

export interface JourneySection {
  /** Section heading shown on the watch page. */
  heading: string;
  /** One-paragraph description of this part of the journey. */
  body: string;
  /** Matching chapter start time in seconds (for the "Watch this part" link). */
  startSeconds: number;
}

export const VIDEOS: VideoRecord[] = [
  // ---- SAINT TMS (explainer series) ----
  {
    slug: "saint-tms-explained",
    id: "j7uXq9Cj2sw",
    title: "SAINT® TMS Explained",
    description:
      "What SAINT® is, how it differs from standard TMS, and what research shows. SAINT® is an FDA-cleared, fMRI-guided form of TMS completed in five days for adults with major depressive disorder who have not improved on antidepressants.",
    category: "SAINT TMS",
  },
  {
    slug: "is-saint-right-for-you",
    id: "KPS8jzgMBPQ",
    title: "Is SAINT® TMS Right for You?",
    description:
      "Tried antidepressants without enough relief? This video walks through the SAINT® eligibility criteria so you can see whether this FDA-cleared, five-day, fMRI-guided treatment may be appropriate for you.",
    category: "SAINT TMS",
  },
  {
    slug: "saint-tms-cost-and-insurance",
    id: "_zxqqypTutc",
    title: "SAINT® TMS Cost & Insurance",
    description:
      "Straight answers about self-pay, insurance, billing codes, and the steps to start SAINT® at Neuro Wellness TMS Centers of America in Coral Springs, FL.",
    category: "SAINT TMS",
  },
  {
    slug: "what-is-saint-tms",
    id: "V1xI1rOP-Ec",
    title: "What Is SAINT® TMS?",
    description:
      "An overview of the SAINT® protocol — how fMRI-guided targeting and an accelerated five-day schedule work together for treatment-resistant depression.",
    category: "SAINT TMS",
  },
  {
    slug: "how-saint-is-performed",
    id: "I2LvKhuWTbA",
    title: "How SAINT® Is Performed",
    description:
      "A closer look at what a SAINT® treatment session involves, from mapping to the short, repeated stimulation sessions delivered across five consecutive days.",
    category: "SAINT TMS",
  },
  {
    slug: "saint-tms-overview",
    id: "nDnqZdNwlXA",
    title: "SAINT® TMS Overview",
    description:
      "An overview of SAINT® TMS and how it differs from conventional TMS — the targeting approach, the accelerated schedule, and the research background.",
    category: "SAINT TMS",
  },
  {
    slug: "saint-tms-webinar",
    id: "vCTrEayL4pk",
    title: "SAINT TMS Explained | Neuro Wellness Webinar",
    description:
      "An in-depth webinar explaining the SAINT® protocol, the patient journey, and what to expect from accelerated, fMRI-guided TMS for treatment-resistant depression.",
    category: "SAINT TMS",
  },
  {
    slug: "the-saint-tms-patient-journey",
    id: "066duR1x64E",
    title: "The SAINT TMS Patient Journey",
    description:
      "Follow the SAINT® TMS patient journey — from the first consultation through the fMRI brain scan and the five-day treatment week at Neuro Wellness TMS Centers of America.",
    category: "SAINT TMS",
  },
  {
    slug: "founder-drive-is-gone",
    id: "xX-68gy7yr4",
    title: "SAINT® TMS for Founders",
    description:
      "For founders and leaders whose depression hasn't improved with medication: how SAINT® — an FDA-cleared, fMRI-guided form of TMS completed in five days — may help you get back the drive to lead.",
    category: "SAINT TMS",
  },
  // ---- SAINT TMS explainer (resource pages) ----
  {
    slug: "saint-tms-explained-short",
    id: "rfB1nX-jqV4",
    title: "SAINT TMS Explained",
    description:
      "A short explainer covering the SAINT® protocol, fMRI-guided targeting, and the accelerated five-day treatment schedule for treatment-resistant depression.",
    category: "TMS Education",
  },
  // ---- TMS education ----
  {
    slug: "tms-therapy-overview",
    id: "1q4E_myye2U",
    title: "TMS Therapy Overview Video",
    description:
      "An overview of transcranial magnetic stimulation (TMS) therapy — how it works, what a session feels like, and the brain circuitry it targets.",
    category: "TMS Education",
  },
  // ---- Patient stories ----
  {
    slug: "tamis-saint-experience",
    id: "_moGR41xPE0",
    title: "Tami's Life-Changing SAINT® Experience",
    description:
      "Tami shares how decades of depression finally met meaningful improvement in just a few days with the SAINT® protocol — a powerful example of how accelerated TMS can make lasting change.",
    category: "Patient Stories",
  },
  {
    slug: "jeannies-saint-tms-journey",
    id: "ZTE7dEEYzeY",
    title: "Jeannie's SAINT® TMS Journey",
    description:
      "Jeannie traveled far after battling long-term, treatment-resistant depression — and found rapid relief through SAINT® at Neuro Wellness TMS Centers of America.",
    category: "Patient Stories",
  },
  {
    slug: "colleens-story-tms-therapy",
    id: "MGgFNtmHA6Q",
    title: "Colleen's Story — TMS Therapy",
    description:
      "Colleen shares her experience with TMS therapy and how it helped her depression after medication alone wasn't enough.",
    category: "Patient Stories",
  },
  {
    slug: "saint-patient-journey",
    id: "pZRgDnrmMvI",
    title: "The SAINT® Patient Journey",
    description:
      "Follow a patient through the SAINT® experience — from the first consultation and fMRI brain scan to the five-day accelerated TMS treatment week at Neuro Wellness TMS Centers of America in Coral Springs, FL.",
    category: "Patient Stories",
    chapters: [
      { name: "Where the journey begins", startSeconds: 0 },
      { name: "First conversation and insurance check", startSeconds: 35 },
      { name: "Virtual visit with a physician", startSeconds: 45 },
      { name: "Day 1: Brain mapping with functional MRI", startSeconds: 62 },
      {
        name: "How a functional MRI differs from a regular MRI",
        startSeconds: 79,
      },
      { name: "Finding your personalized treatment target", startSeconds: 169 },
      {
        name: "Reviewing the treatment plan with the physician",
        startSeconds: 203,
      },
      { name: "How each treatment session works", startSeconds: 212 },
      { name: "Comfort and monitoring during treatment", startSeconds: 232 },
      { name: "Resting between sessions", startSeconds: 250 },
      { name: "Day 2", startSeconds: 274 },
      { name: "Day 3", startSeconds: 295 },
      { name: "Day 4", startSeconds: 316 },
      { name: "Day 5: Completing treatment", startSeconds: 331 },
      { name: "How to begin", startSeconds: 378 },
    ],
    journeySections: [
      {
        heading: "First call and insurance check",
        body: "Your journey starts with a phone call to our care team. We listen to your history, answer your questions, and verify your insurance benefits at no cost to you — so you know what SAINT® is likely to cost before you commit to anything.",
        startSeconds: 35,
      },
      {
        heading: "Virtual visit with the physician",
        body: "You meet Dr. Daniel Pietras, MD, by Zoom. He reviews your depression history, the medications you've tried, and whether SAINT® — an FDA-cleared, fMRI-guided form of TMS — is appropriate for you. This visit is free.",
        startSeconds: 45,
      },
      {
        heading: "Day 1: Brain-mapping MRI",
        body: "Before any stimulation, you have a resting-state functional MRI (fMRI). Unlike a regular MRI that shows structure, fMRI shows how regions of your brain are communicating. Specialized software uses this map to find your individualized treatment target in the left dorsolateral prefrontal cortex.",
        startSeconds: 62,
      },
      {
        heading: "Finding the personalized target",
        body: "Your brain's functional connectivity is unique. The fMRI data is processed by the FDA-cleared targeting software to compute the exact spot on your prefrontal cortex where stimulation is most likely to help your depression.",
        startSeconds: 169,
      },
      {
        heading: "Reviewing the brain map with the physician",
        body: "Dr. Pietras reviews your personalized brain map with you and explains the treatment plan — where stimulation will be delivered, how many sessions, and what to expect each day.",
        startSeconds: 203,
      },
      {
        heading: "Treatment sessions",
        body: "Each 10-minute session delivers intermittent theta-burst stimulation (iTBS) to your personalized target. You sit in a comfortable chair, awake and alert, while the magnetic coil delivers the pulses.",
        startSeconds: 212,
      },
      {
        heading: "Breaks between sessions",
        body: "Between each of the ten daily sessions there's roughly a 50-minute break. Most people rest, read, or watch TV. You stay at the clinic for the full day but you're not in a hospital — it's an outpatient setting.",
        startSeconds: 250,
      },
      {
        heading: "Days 2 through 5",
        body: "The same schedule repeats for five consecutive days. The team checks in with you throughout, tracking your mood and any side effects. By Day 5, the full accelerated course of about 90,000 pulses is complete.",
        startSeconds: 274,
      },
      {
        heading: "Follow-up after the week",
        body: "After Day 5, you return home. We schedule follow-up visits to track your response over the following weeks and coordinate with your local doctor or therapist. If you're traveling from out of state, follow-up can be done by video.",
        startSeconds: 331,
      },
    ],
  },
  // ---- Prism (PTSD) ----
  {
    slug: "prism-how-it-works",
    id: "OLMPaAsWocA",
    title: "Prism for PTSD — How It Works",
    description:
      "How Prism, an EEG-based neurofeedback therapy for PTSD, uses a brain signal to train regulation of the amygdala-related activity behind many PTSD symptoms.",
    category: "Prism",
  },
  {
    slug: "prism-what-to-expect",
    id: "zNPtoMKS2j4",
    title: "Prism — What to Expect",
    description:
      "What a Prism session feels like — the EEG cap, the calibration, the five cycles, and what your operator reviews with you after each session.",
    category: "Prism",
  },
  {
    slug: "prism-clinical-research",
    id: "FPKIzH0vWWE",
    title: "Prism — Clinical Research Overview",
    description:
      "A review of the clinical research behind Prism for PTSD, including the rates of clinically significant improvement and remission seen in the study.",
    category: "Prism",
  },
  {
    slug: "prism-patient-experience",
    id: "mCOHM-B7MpY",
    title: "Prism — Patient Experience",
    description:
      "A patient's perspective on going through the Prism neurofeedback protocol for PTSD at Neuro Wellness TMS Centers of America.",
    category: "Prism",
  },
];

export function findVideoBySlug(slug: string): VideoRecord | undefined {
  return VIDEOS.find((v) => v.slug === slug);
}

export function findVideoById(id: string): VideoRecord | undefined {
  return VIDEOS.find((v) => v.id === id);
}
